import React, { createContext, useContext, useEffect, useState } from 'react';
import { quotaBus, QuotaExceededPayload } from '../services/quotaBus';
import { useAuth } from './AuthContext';

/**
 * Global "plan quota exceeded" state (docs/usage-plans-design.md §7.3).
 *
 * Populated from three sources:
 *  - the ApiService 429 interceptor (any REST call rejected with quota_exceeded)
 *  - the chat SSE "quota_exceeded" terminal frame (via quotaBus)
 *  - a one-time /api/usage/status fetch on login, so a paused metric is known
 *    after a page reload without waiting for the next rejection.
 *
 * Consumed by the AppLayout quota banner and the chat composer disable.
 */

interface QuotaContextType {
  /** metric -> latest quota_exceeded payload (only enforced-paused metrics). */
  paused: Record<string, QuotaExceededPayload>;
  clear: () => void;
}

const QuotaContext = createContext<QuotaContextType>({ paused: {}, clear: () => {} });

export const useQuota = () => useContext(QuotaContext);

export const QuotaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [paused, setPaused] = useState<Record<string, QuotaExceededPayload>>({});

  useEffect(() => quotaBus.subscribe((payload) => {
    setPaused((prev) => ({ ...prev, [payload.metric]: payload }));
  }), []);

  // Seed from the usage summary once per login so paused state survives reloads.
  useEffect(() => {
    if (!currentUser) { setPaused({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const { usageService } = await import('../services/UsageService');
        const summary = await usageService.getStatus();
        if (cancelled || !summary?.metrics) return;
        const seeded: Record<string, QuotaExceededPayload> = {};
        (Object.keys(summary.metrics) as Array<keyof typeof summary.metrics>).forEach((metric) => {
          const entry = summary.metrics[metric];
          if (entry?.paused_until) {
            seeded[metric as string] = {
              error: 'quota_exceeded',
              metric: metric as string,
              used: entry.used,
              limit: entry.limit,
              plan_id: summary.plan?.id ?? null,
              resets_at: entry.paused_until,
            };
          }
        });
        if (Object.keys(seeded).length) setPaused((prev) => ({ ...seeded, ...prev }));
      } catch { /* dashboardless orgs / stale backends: stay silent */ }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  return (
    <QuotaContext.Provider value={{ paused, clear: () => setPaused({}) }}>
      {children}
    </QuotaContext.Provider>
  );
};
